import type {AhpSpaceSelection} from '@github-ui/ahp-session-client/contracts'
import type {AutoTierPickerConfig} from '@github-ui/copilot-shared-ui/components/chat-model-picker/ModelPickerDesign'
import type {ModelUpdateOptions} from '@github-ui/copilot-shared-ui/contexts/EstablishedAhpComposerContext'
import type {ReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import type {IconButtonProps} from '@primer/react'
import type {ReactNode} from 'react'

import {SpacePicker} from '../spaces/SpacePicker'
import {ModelPicker} from '../task/ModelPicker'
import {ReasoningEffortPicker} from '../task/ReasoningEffortPicker'

export interface ChatModelControlsProps {
  isLoading?: boolean
  selectedModelId?: string
  onUpdateModel: (modelId: string, options?: ModelUpdateOptions) => void
  selectedReasoningEffort?: ReasoningEffort
  onUpdateReasoningEffort: (effort: ReasoningEffort | undefined, supportedEfforts?: readonly string[]) => void
  supportedReasoningEfforts?: readonly string[]
  leadingControl?: ReactNode
  trailingControl?: ReactNode
  modelWrapperClassName?: string
  isTokenBasedBillingEnabled?: boolean
  autoTierPicker?: AutoTierPickerConfig
  /** Opts the pickers into the container-query compaction the narrow composers use. */
  responsiveIconOnly?: boolean
  tooltipDirection?: IconButtonProps['tooltipDirection']
}

export function ChatModelControls({
  isLoading,
  selectedModelId,
  onUpdateModel,
  selectedReasoningEffort,
  onUpdateReasoningEffort,
  supportedReasoningEfforts,
  leadingControl,
  trailingControl,
  modelWrapperClassName,
  isTokenBasedBillingEnabled,
  autoTierPicker,
  responsiveIconOnly,
  tooltipDirection,
}: ChatModelControlsProps) {
  const modelPicker = (
    <ModelPicker
      selectedModelId={selectedModelId}
      onUpdateModel={onUpdateModel}
      showLoadingState={isLoading}
      showTriggerIcon={false}
      size="medium"
      isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
      autoTierPicker={autoTierPicker}
      responsiveIconOnly={responsiveIconOnly}
      tooltipDirection={tooltipDirection}
    />
  )
  return (
    <>
      {leadingControl}
      {modelWrapperClassName ? <div className={modelWrapperClassName}>{modelPicker}</div> : modelPicker}
      <ReasoningEffortPicker
        selectedReasoningEffort={selectedReasoningEffort}
        onUpdateReasoningEffort={onUpdateReasoningEffort}
        supportedReasoningEfforts={supportedReasoningEfforts}
        responsiveIconOnly={responsiveIconOnly}
        tooltipDirection={tooltipDirection}
      />
      {trailingControl}
    </>
  )
}

export interface ChatSpaceControlProps {
  selectedSpace?: AhpSpaceSelection
  onSelectSpace: (space: AhpSpaceSelection | undefined) => void
  isResolving?: boolean
  onResolvingChange?: (isResolving: boolean) => void
  selectionError?: string
  onSelectionErrorChange?: (message: string | undefined) => void
  disabled?: boolean
  portalContainerName?: string
}

const ignoreResolvingChange = () => undefined
const ignoreSelectionError = () => undefined

export function ChatSpaceControl({
  selectedSpace,
  onSelectSpace,
  isResolving = false,
  onResolvingChange = ignoreResolvingChange,
  selectionError,
  onSelectionErrorChange = ignoreSelectionError,
  disabled,
  portalContainerName,
}: ChatSpaceControlProps) {
  return (
    <SpacePicker
      selectedSpace={selectedSpace}
      onSelectSpace={onSelectSpace}
      isResolving={isResolving}
      onResolvingChange={onResolvingChange}
      selectionError={selectionError}
      onSelectionErrorChange={onSelectionErrorChange}
      disabled={disabled}
      portalContainerName={portalContainerName}
    />
  )
}
